import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

import { stockKeys } from "./query-keys";

type Marketplace = "shopee" | "lazada" | "tiktok";
type SyncResult = components["schemas"]["SyncResult"];

export interface SyncTarget {
  sku: string;
  marketplace: Marketplace;
}

export interface SyncMultiVars {
  targets: SyncTarget[];
  seller_id?: string;
}

/**
 * Bulk "Sync Multi" (PRD v1.8 §4.6.3.2-C).
 *
 * A single batched push to the marketplace for an explicit list of (sku, marketplace)
 * targets. Partial success is HTTP 200 — individual items may carry `ok:false`
 * with a per-item message, so the caller renders a result table rather than a
 * single success/failure toast.
 */
export function useSyncMulti() {
  const queryClient = useQueryClient();
  return useMutation<SyncResult, Error, SyncMultiVars>({
    meta: { silentError: true },
    mutationFn: async ({ targets, seller_id }) => {
      const trimmed = seller_id?.trim();
      const { data, error } = await apiClient.POST("/v1/stock/sync", {
        body: { targets, ...(trimmed ? { seller_id: trimmed } : {}) },
      });
      if (error) throw error;
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: stockKeys.lists });
    },
  });
}
